<script>
import OrgChartNode4 from './OrgChartNode4.vue';
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "../../PhotoBox.vue";

export default {
    name: "OrgChartTemp4",
    components: {
        OrgChartNode4,
        SpellCheckedText,
        PhotoBox
    },
    props: {
        orgChart: { type: Object, required: true },
        startPage: { type: Number, default: 1 },
    },
    data() {
        return {
            NODE_WIDTH: 210,
            NODE_HEIGHT: 70,
            LEVEL_GAP: 30,
            AVAILABLE_WIDTH: 640,
            AVAILABLE_HEIGHT: 850
        };
    },
    computed: {
        treeData() {
            return this.buildTree(this.orgChart.members);
        },
        treeScale() {
            const requiredWidth = this.getTreeWidth(this.treeData);
            const maxDepth = this.getMaxDepth(this.treeData);
            const requiredHeight = maxDepth * this.NODE_HEIGHT + (maxDepth - 1) * this.LEVEL_GAP;

            const widthScale = requiredWidth > this.AVAILABLE_WIDTH ? Math.max(0.3, this.AVAILABLE_WIDTH / requiredWidth) : 1;
            const heightScale = requiredHeight > this.AVAILABLE_HEIGHT ? Math.max(0.3, this.AVAILABLE_HEIGHT / requiredHeight) : 1;

            return Math.min(widthScale, heightScale);
        }
    },
    methods: {
        buildTree(members) {
            const map = {};
            const roots = [];

            if (!members || members.length === 0) return [];

            const membersCopy = members.map(member => ({
                ...member,
                sourceMember: member,
                children: []
            }));

            // Create a map for quick lookup
            membersCopy.forEach(member => {
                map[member.teamMemberId] = member;
            });

            // Attach children to parents
            membersCopy.forEach(member => {
                if (member.reportsToId && map[member.reportsToId]) {
                    map[member.reportsToId].children.push(member);
                } else {
                    roots.push(member);
                }
            });

            return roots;
        },
        getMaxDepth(nodes) {
            if (!nodes || nodes.length === 0) return 0;
            let maxDepth = 1;
            nodes.forEach(node => {
                if (node.children && node.children.length > 0) {
                    maxDepth = Math.max(maxDepth, 1 + this.getMaxDepth(node.children));
                }
            });
            return maxDepth;
        },
        getTreeWidth(nodes) {
            if (!nodes || nodes.length === 0) return 0;

            let totalWidth = 0;

            nodes.forEach(node => {
                if (!node.children || node.children.length === 0) {
                    totalWidth += this.NODE_WIDTH;
                } else {
                    // A parent's width is the max of its own width OR the sum of its children's widths
                    const childrenWidth = this.getTreeWidth(node.children);
                    totalWidth += Math.max(this.NODE_WIDTH, childrenWidth);
                }
            });

            return totalWidth;
        }
    }
};
</script>

<template>
    <div class="main-container page-unit" :style="{
        '--header-color': orgChart.style.colorScheme && orgChart.style.colorScheme.header,
        '--body-color': orgChart.style.colorScheme && orgChart.style.colorScheme.body,
        '--footer-color': orgChart.style.colorScheme && orgChart.style.colorScheme.footer,
        '--heading-color': orgChart.style.heading.color,
        '--heading-size': orgChart.style.heading.size + 'px',
        '--heading-weight': orgChart.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': orgChart.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': orgChart.style.heading.underline
            ? 'underline'
            : 'none',
        '--heading-font': orgChart.style.heading.font,
        '--label-color': orgChart.style.label.color,
        '--label-size': orgChart.style.label.size + 'px',
        '--label-weight': orgChart.style.label.bold ? 'bold' : 'normal',
        '--label-weight-bold': orgChart.style.label.bold ? '800' : '600',
        '--label-style': orgChart.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': orgChart.style.label.underline
            ? 'underline'
            : 'none',
        '--label-font': orgChart.style.label.font,
        '--text-color': orgChart.style.text.color,
        '--text-size': orgChart.style.text.size + 'px',
        '--text-weight': orgChart.style.text.bold ? 'bold' : 'normal',
        '--text-style': orgChart.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': orgChart.style.text.underline ? 'underline' : 'none',
        '--text-font': orgChart.style.text.font,
    }">
        <!-- Header (Matched with CPTemp6) -->
        <div class="header-banner banner">
            <div class="header-content">
                <h2 class="header-title">
                    <SpellCheckedText :value="orgChart.title || ''" @input="orgChart.title = $event" />
                </h2>
                <div class="header-logo photobox">
                    <PhotoBox :project="orgChart" type="logo" bgColor="transparent" objectFit="contain" />
                </div>
            </div>
        </div>

        <div class="content-area">
            <div class="about-section">
                <h1 class="page-title">
                    <SpellCheckedText :value="orgChart.about.title || ''" @input="orgChart.about.title = $event" />
                </h1>
                <p class="description">
                    <SpellCheckedText :value="orgChart.about && orgChart.about.description || ''"
                        @input="orgChart.about.description = $event" />
                </p>
            </div>

            <!-- Recursive Org Chart Tree -->
            <div class="tree-container">
                <ul class="tree" :style="{ transform: `scale(${treeScale})`, transformOrigin: 'top center' }">
                    <OrgChartNode4 v-for="node in treeData" :key="node.teamMemberId" :node="node" />
                </ul>
            </div>
        </div>

        <!-- Footer (Matched with CPTemp6) -->
        <div class="footer-bottom">
            <div class="footer-content">
                <div class="footer-left">
                    <div class="footer-line"></div>
                    <p class="footer-text">
                        <SpellCheckedText :value="orgChart.opprName || ''" @input="orgChart.opprName = $event" />
                    </p>
                </div>
               <div class="footer-right">
                 <span class="page-number">{{ String(startPage).padStart(2, '0') }}</span>
                </div>				
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 211mm;
    height: 296.83mm;
    position: relative;
    box-sizing: border-box;
    background: white;

    &:hover {
        ::v-deep .node-card .photobox .photo-overlay {
            opacity: 1;
        }

        ::v-deep .node-card .photobox img {
            opacity: 0.9;
        }
    }
}

.banner {
    height: 100px;
    display: flex;
    align-items: center;
    padding: 0 50px;

    &.header-banner {
        background: var(--header-color);
    }
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    .header-title {
        color: white;
        font-size: 16px;
        font-weight: 700;
        margin: 0;
        font-family: Helvetica;
    }

    .header-logo {
        height: 80px;
        width: 120px;
    }
}

.content-area {
    padding: 30px 40px;
    flex: 1;
    overflow: hidden;

    .about-section {
        height: fit-content;
        max-height: 180px;
        margin-bottom: 20px;
        overflow: hidden;
		text-align:left;
    }

    .page-title {
        color: var(--heading-color);
        font-size: var(--heading-size);
        font-family: var(--heading-font);
        font-weight: var(--heading-weight);
        font-style: var(--heading-style);
        text-decoration: var(--heading-decoration);
        margin-bottom: 10px;
    }

    .description {
        color: var(--text-color);
        font-size: var(--text-size);
        font-family: var(--text-font);
        font-style: var(--text-style);
        text-decoration: var(--text-decoration);
        white-space: pre-wrap;
        line-height: 1.5;
        margin-bottom: 0px;
    }
}

.tree-container {
    width: 100%;
    display: flex;
    justify-content: center;
}

// The root ul — uses same CSS connector pattern as Temp1/Temp2
.tree {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-left: 0;
    margin: 0;

    // All nested ul (children lists)
    ::v-deep ul {
        padding-top: 10px;
        position: relative;
        display: flex;
        justify-content: center;
        padding-left: 0;
        margin: 0;

        // Vertical line down from parent to the horizontal bar
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 50%;
            border-left: 1.5px solid #555;
            width: 0;
            height: 10px;
        }
    }

    // All li items (including nested)
    ::v-deep li {
        text-align: center;
        list-style-type: none;
        position: relative;
        padding: 10px 8px 0 8px;

        // Horizontal connecting line segments
        &::before,
        &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 50%;
            border-top: 1.5px solid #555;
            width: 50%;
            height: 30px;
        }

        &::after {
            right: auto;
            left: 50%;
            border-left: 1.5px solid #555;
        }

        // Single child — hide horizontal lines, remove top padding
        &:only-child::after,
        &:only-child::before {
            display: none;
        }

        &:only-child {
            padding-top: 0;
        }

        // First and last child edge cleanup
        &:first-child::before,
        &:last-child::after {
            border: 0 none;
        }

        &:last-child::before {
            border-right: 1.5px solid #555;
            border-radius: 0 5px 0 0;
        }

        &:first-child::after {
            border-radius: 5px 0 0 0;
        }
    }
}

.footer-bottom {
    margin-top: auto;
    margin-bottom: 40px;

    .footer-content {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .footer-left {
            flex: 1;
            margin: 0 20px 0px 50px;

            .footer-line {
                height: 2px;
                background: var(--footer-color);
            }

            .footer-text {
                margin-top: 10px;
                margin-bottom: 0px;
                font-size: 14px;
                color: black;
                font-family: Helvetica;
				text-align: left;
            }
        }

        .footer-right {
              .page-number {
                position: absolute;
                right: 15px;
                bottom: 10px;
                font-size: 14px;
                font-weight: 700;
                font-family: Helvetica;
            }
        }
    }
}
</style>
